import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IHealthFundCardLookup } from '@principle-theorem/principle-core';
import {
  IDVACard,
  IHealthFundProvider,
  IMedicareCard,
  IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  MONTH_YEAR_FORMAT,
  WithRef,
} from '@principle-theorem/shared';

export interface IUpdateHealthCardsRequest {
  patient: WithRef<IPatient>;
  medicareCard?: IMedicareCard;
  healthFundCard?: IHealthFundCardLookup;
  dvaCard?: IDVACard;
}

@Component({
  selector: 'pr-update-health-cards',
  templateUrl: './update-health-cards.component.html',
  styleUrls: ['./update-health-cards.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UpdateHealthCardsComponent {
  dateFormat = MONTH_YEAR_FORMAT;
  selectedHealthFundCode = new FormControl<string>('', { nonNullable: true });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IUpdateHealthCardsRequest,
    private _dialogRef: MatDialogRef<UpdateHealthCardsComponent, void>
  ) {
    this._initialiseSelectedFund();
  }

  get matchingFunds(): IHealthFundProvider[] {
    return this.data.healthFundCard?.matchingFunds ?? [];
  }

  get hasMultipleMatches(): boolean {
    return this.matchingFunds.length > 1;
  }

  async updateAll(): Promise<void> {
    const updates: Partial<IPatient> = {};
    if (this.data.medicareCard) {
      updates.medicareCard = this.data.medicareCard;
    }
    if (this.data.dvaCard) {
      updates.dvaCard = this.data.dvaCard;
    }
    if (this.data.healthFundCard) {
      // Use selectedFundCode instead of original fundCode
      updates.healthFundCard = {
        membershipNumber: this.data.healthFundCard.membershipNumber,
        memberNumber: this.data.healthFundCard.memberNumber,
        fundCode:
          this.selectedHealthFundCode.value ||
          this.data.healthFundCard.fundCode,
      };
    }
    await Firestore.patchDoc(this.data.patient.ref, updates);
    this._dialogRef.close();
  }

  private _initialiseSelectedFund(): void {
    const cardWithMatches = this.data.healthFundCard;
    if (!cardWithMatches?.matchingFunds?.length) {
      this.selectedHealthFundCode.setValue(
        this.data.healthFundCard?.fundCode ?? ''
      );
      return;
    }

    // Check if patient's existing fund is in the match list (case-insensitive)
    const existingFund = this.data.patient.healthFundCard?.fundCode;
    if (existingFund) {
      const matchInList = cardWithMatches.matchingFunds.find(
        (matchingFund) =>
          matchingFund.name.toLowerCase() === existingFund.toLowerCase()
      );
      if (matchInList) {
        this.selectedHealthFundCode.setValue(matchInList.name);
        return;
      }
    }

    // Default to first match
    this.selectedHealthFundCode.setValue(
      cardWithMatches.matchingFunds[0]?.name ?? ''
    );
  }
}
